import { DeleteIcon } from "@chakra-ui/icons";
import {
  Tr,
  Td,
  VStack,
  Text,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  useDisclosure,
} from "@chakra-ui/react";
import {
  AccountAccessRoleName,
  AccountPermission,
  roleHasPermission,
  AccountAccessRole,
} from "@syla/shared/types/models/AccountAccessBase";
import { AccountAccessWithUserPopulated } from "@syla/shared/types/responses/GetAccountsResponse";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { HiDotsVertical } from "react-icons/hi";
import { RemoveBillingOwnerModal } from "../../../components/organisms/accounts/RemoveBillingOwnerModal";
import { useUserContext } from "../../../contexts/UserContext";
import { hasAccountPermission } from "../../../helper/permissions";
import { useReportError } from "../../../helpers/useReportError";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { useMutationUpdateAccountAccess } from "../../../store/useMutationUpdateAccountAccess";
import { Account } from "../../../types/Account";
import { AccountAccessRoleSelector } from "../AccountAccessRoleSelector";
import { DowngradeOwnAccountAccessModal } from "./DowngradeOwnAccountAccessModal";
import { RemoveAccountAccessModal } from "./RemoveAccountAccessModal";

export function AccountAccessRow({
  account,
  accountAccess,
}: {
  account: Account;
  accountAccess: AccountAccessWithUserPopulated;
}) {
  const { userDetails } = useUserContext();

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const ownAccess = accountAccess.user._id === userDetails?.id;

  const mutationUpdateAccountAccess = useMutationUpdateAccountAccess(accountId);

  const reportError = useReportError();

  const removeAccountAccessDisclosure = useDisclosure();
  const removeBillingOwnerDisclosure = useDisclosure();

  const [pendingRole, setPendingRole] = useState<AccountAccessRole>();
  const downgradeOwnAccessDisclosure = useDisclosure();

  const formMethods = useForm<AccountAccessWithUserPopulated>({
    defaultValues: accountAccess,
  });
  const { watch, setValue, reset } = formMethods;

  useEffect(() => {
    reset(accountAccess);
  }, [accountAccess, reset, account]);

  const [isSaving, setIsSaving] = useState(false);

  const requiresDowngradeConfirmation = useCallback(
    (newRole: AccountAccessRole) => {
      const permissionChanged = (permission: AccountPermission) =>
        roleHasPermission(accountAccess.role, permission) &&
        !roleHasPermission(newRole, permission);

      return (
        ownAccess &&
        (permissionChanged(AccountPermission.ChangeOwnership) ||
          permissionChanged(AccountPermission.ChangeAccess))
      );
    },
    [accountAccess.role, ownAccess],
  );

  const hasPermissionToRemoveMember =
    ownAccess ||
    (accountAccess.role != AccountAccessRole.Owner &&
      hasAccountPermission(account, AccountPermission.ChangeAccess)) ||
    (accountAccess.role == AccountAccessRole.Owner &&
      hasAccountPermission(account, AccountPermission.ChangeOwnership));

  const isBillingOwner = account.subscriptions.some(
    (sub) => sub.billingOwner?._id == accountAccess.user._id,
  );

  const updateRole = async (role: AccountAccessRole) => {
    setValue("role", role);

    try {
      setIsSaving(true);

      await mutationUpdateAccountAccess.mutateAsync({
        ops: [
          {
            userId: accountAccess.user._id!,
            action: "set",
            role,
          },
        ],
      });
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Tr key={accountAccess._id}>
        <Td>
          <VStack w="100%" alignItems="flex-start">
            {accountAccess.user.fullName && (
              <Text>{accountAccess.user.fullName}</Text>
            )}
            <Text>
              {accountAccess.user.email}
              {ownAccess ? " (You)" : ""}
            </Text>
          </VStack>
        </Td>

        <Td>
          <Text>{isBillingOwner ? "Yes" : ""}</Text>
        </Td>

        <Td width="15rem">
          {
            (
              (accountAccess.role != AccountAccessRole.Owner &&
                hasAccountPermission(
                  account,
                  AccountPermission.ChangeAccess,
                )) ||
              (accountAccess.role == AccountAccessRole.Owner &&
                hasAccountPermission(
                  account,
                  AccountPermission.ChangeOwnership,
                ))
            ) ?
              <AccountAccessRoleSelector
                currentRole={watch("role")}
                onSelected={async (role) => {
                  if (requiresDowngradeConfirmation(role)) {
                    setPendingRole(role);
                    downgradeOwnAccessDisclosure.onOpen();
                    return;
                  }

                  await updateRole(role);
                }}
                isLoading={isSaving}
                width="100%"
              />
              // otherwise just display
            : <Text>{AccountAccessRoleName[watch("role")]}</Text>
          }
        </Td>

        {/* Invite Status */}
        <Td>Accepted</Td>

        <Td textAlign="right" width="5rem">
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<HiDotsVertical />}
              backgroundColor="transparent"
              isDisabled={!hasPermissionToRemoveMember}
            />
            <MenuList p="0">
              {hasPermissionToRemoveMember && (
                <MenuItem
                  textColor="red.600"
                  onClick={() =>
                    isBillingOwner ?
                      removeBillingOwnerDisclosure.onOpen()
                    : removeAccountAccessDisclosure.onOpen()
                  }
                  icon={<DeleteIcon />}
                >
                  {isBillingOwner ? "Remove billing owner" : "Remove member"}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </Td>
      </Tr>
      {removeBillingOwnerDisclosure.isOpen && (
        <RemoveBillingOwnerModal
          accountId={accountId}
          billingOwnerId={accountAccess.user._id}
          disclosure={removeBillingOwnerDisclosure}
        />
      )}
      {removeAccountAccessDisclosure.isOpen && (
        <RemoveAccountAccessModal
          account={account}
          accountId={accountId}
          accountAccess={accountAccess}
          ownAccess={ownAccess}
          disclosure={removeAccountAccessDisclosure}
        />
      )}
      {downgradeOwnAccessDisclosure.isOpen && pendingRole && (
        <DowngradeOwnAccountAccessModal
          accountId={accountId}
          accountAccess={accountAccess}
          newRole={pendingRole}
          disclosure={downgradeOwnAccessDisclosure}
          onSuccess={(role) => setValue("role", role)}
        />
      )}
    </>
  );
}
